import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TaskDetail } from '@app/interfaces/lm/task';
import { HardwareReservation } from '@app/interfaces/reservation/hardware-reservation';
import { TimeSegment } from '@app/interfaces/reservation/human-reservation';
import { TaskHardwareService } from '@services/lm/task-hardware.service';
import { TaskHumanService } from '@services/lm/task-human.service';
import { differenceInCalendarDays } from 'date-fns';
import dayjs from 'dayjs';
import { DisabledTimeFn } from 'ng-zorro-antd/date-picker';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
    selector: 'app-edit-reservation',
    templateUrl: './edit-reservation.component.html',
    styleUrls: ['./edit-reservation.component.less'],
})
export class EditReservationComponent implements OnInit {
    @Input() taskDetail!: TaskDetail;
    @Input() resourceType = 'hardware';
    @Output() submitSuccess = new EventEmitter();
    isVisible = false;
    submitLoading = false;
    loading = false;
    validateForm!: FormGroup;
    listOfControl: any[] = [];
    editData: Array<HardwareReservation> = [];
    timeSegmentArr: {
        startTime: number;
        endTime: number;
        duration: number;
        startDisabled?: boolean;
        endDisabled?: boolean;
    }[] = [];
    hardwareId!: string;
    title = 'Reserve By Date';
    constructor(private fb: FormBuilder,
                private taskHardwareService: TaskHardwareService,
                private taskHumanService: TaskHumanService,
                private message: NzMessageService) {
    }

    ngOnInit(): void {
        this.validateForm = this.fb.group({});
    }

    disabledPicker(time: number): boolean {
        const today = new Date();
        return !time || dayjs(time).isBefore(today);
    }

    disabledDate = (current: Date): boolean => {
        const { startTime, dueTime } = this.taskDetail;
        const endTime = dayjs(dueTime).add(1, 'second').valueOf();
        return differenceInCalendarDays(current, new Date(startTime)) < 0 ||
            differenceInCalendarDays(current, new Date()) < 0 ||
            differenceInCalendarDays(current, new Date(endTime)) > 0;
    }

    disabledTime?: DisabledTimeFn = (date) => {
        let disabledHours: number[] = [];
        const { dueTime } = this.taskDetail;
        const endTime = dayjs(dueTime).add(1, 'second');
        if (date) {
            const today = dayjs().format('YYYY-MM-DD');
            const selectDate = dayjs(date as Date).format('YYYY-MM-DD');
            if (dayjs(selectDate).isSame(today)) {
                disabledHours = this.range(0, dayjs().add(1, 'hour').get('hour'));
            }
            if (dayjs(selectDate).isSame(dayjs(endTime).format('YYYY-MM-DD'))) {
                disabledHours = this.range(dayjs(endTime).get('hour') + 1, 24);
            }
        }
        return {
            nzDisabledHours: () => disabledHours,
            nzDisabledMinutes: () => [],
            nzDisabledSeconds: () => [],
        };
    }

    range(start: number, end: number): number[] {
        const result: number[] = [];
        for (let i = start; i < end; i++) {
            result.push(i);
        }
        return result;
    }

    open(hardwareId: string, row: Array<HardwareReservation>, resourceName: string): void {
        this.isVisible = true;
        this.title = resourceName;
        this.listOfControl = [];
        this.timeSegmentArr = [];
        this.validateForm.reset();
        this.validateForm.controls = {};
        this.hardwareId = hardwareId;
        row.forEach((item) => {
            item.timeSegments?.forEach((rangItem) => {
                const endTime = dayjs(rangItem.endTime).add(1, 'hour').startOf('hour').valueOf();
                this.timeSegmentArr.push({
                    ...rangItem,
                    endTime: dayjs(rangItem.endTime).add(1, 'hour').startOf('hour').valueOf(),
                    startDisabled: this.disabledPicker(rangItem.startTime),
                    endDisabled: this.disabledPicker(endTime)
                });
            });
        });
        this.editData = row;
        this.fetchData();
    }

    addField(e?: MouseEvent): void {
        if (e) {
            e.preventDefault();
        }
        const index = this.listOfControl.length > 0 ? this.listOfControl[this.listOfControl.length - 1].index + 1 : 1;

        const control = {
            index,
            startTime: `startTime${index}`,
            endTime: `endTime${index}`,
        };
        this.addControl(control);
    }

    addControl(control: { [key: string]: string | number | TimeSegment }): void {
        const index = this.listOfControl.push(control);
        const needControl = JSON.parse(JSON.stringify(control));
        delete needControl.index;
        if (needControl.id) {
            delete needControl.id;
        }
        if (needControl.value) {
            delete needControl.value;
        }
        Object.keys(needControl).forEach((item) => {
            this.validateForm.addControl(
                this.listOfControl[index - 1][item],
                new FormControl(null, Validators.required),
            );
        });
    }

    async fetchData(): Promise<void> {
        try {
            this.loading = true;
            let levels: TimeSegment[] = [];
            if (this.timeSegmentArr.length > 0) {
                levels = this.timeSegmentArr;
            }
            if (levels.length === 0) {
                this.addField();
                return;
            }

            levels.forEach((item, index) => {
                const control: {
                    [key: string]: string | number | TimeSegment
                } = {
                    index: index + 1,
                    startTime: `startTime${index + 1}`,
                    endTime: `endTime${index + 1}`,
                    value: item
                };
                this.addControl(control);
            });
            this.getFieldData();
        } finally {
            this.loading = false;
        }
    }

    getFieldData(): void {
        if (this.editData) {
            const fieldArray = this.timeSegmentArr;
            const fields: { [key: string]: string } = {};
            fieldArray.forEach((item: any, index: number) => {
                Object.keys(item).forEach((fieldItem) => {
                    const itemKey = fieldItem + String(index + 1);
                    fields[itemKey] = item[fieldItem];
                });
            });
            this.validateForm.reset({
                ...this.editData,
                ...fields,
            });
        }
    }

    close(): void {
        this.isVisible = false;
    }

    removeField(i: { index: number; startTime: number | string; endTime: number | string; }, e: MouseEvent): void {
        e.preventDefault();
        const index = this.listOfControl.indexOf(i);
        this.listOfControl.splice(index, 1);
        Object.values(i).forEach((fieldItem) => {
            this.validateForm.removeControl(fieldItem as string);
        });
    }

    disabledRemove(i: number): boolean {
        const arrLength = this.timeSegmentArr.length;
        return i < arrLength ? this.disabledPicker(this.timeSegmentArr[i].startTime) || this.disabledPicker(this.timeSegmentArr[i].endTime) : false;
    }

    validForm(): boolean {
        for (const control of Object.values(this.validateForm.controls)) {
            control.markAsDirty();
            control.updateValueAndValidity();
        }
        if (!this.validateForm.valid || !this.taskDetail || !this.hardwareId) {
            console.log('not valid', this.validateForm.value);
            return false;
        }
        return true;
    }

    async submit(): Promise<void> {
        try {
            if (!this.validForm()) {
                return ;
            }
            const data = this.validateForm.value;
            const fieldArray = this.listOfControl?.map((item: { [key: string]: string }) => {
                return {
                    startTime: dayjs(data[item.startTime]).startOf('h').valueOf(),
                    endTime: dayjs(data[item.endTime]).subtract(1, 'hour').endOf('h').valueOf()
                };
            });
            if (!fieldArray.every((row) => row.endTime > row.startTime)) {
                this.message.warning('EndTime should more than startTime');
                return ;
            }
            this.loading = true;
            const commonParams = {
                task: this.taskDetail.id,
                timeSegmentOverwriteList: fieldArray
            };
            if (this.resourceType === 'human') {
                await this.taskHumanService.overwriteOne({
                    humanId: this.hardwareId,
                    ...commonParams,
                });
            } else {
                await this.taskHardwareService.overwriteOne({
                    hardwareId: this.hardwareId,
                    ...commonParams
                });
            }
            this.message.success(this.resourceType === 'human' ? 'Human resource edited successfully' : 'The resource edited successfully');
            this.close();
            this.submitSuccess.emit();
        } finally {
            this.loading = false;
        }
    }

}
